import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { ContractConfirmationFacade } from '@zero/shared/feature-contract-confirmation/base';
import { TradingSessionComponent, UserMessagesComponent, UserNotesComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { InstrumentType } from '@zero/shared/util-models';
import { UserMessagesHolderService } from '@zero/shared/util-web-app-services';
import { HasFractionPipe, TradeCreatePageBase } from '@zero/trade/feature-trade/base';
import { combineLatest, filter, map, merge } from 'rxjs';
import { DonauCapitalHintComponent } from '../donau-capital-hint/donau-capital-hint.component';
import { TradeCreateAssetComponent } from '../trade-create-asset/trade-create-asset.component';
import { TradeCreateHeaderComponent } from '../trade-create-header/trade-create-header.component';
import { TradeCreateHintComponent } from '../trade-create-hint/trade-create-hint.component';
import { TradeCreateOptionsComponent } from '../trade-create-options/trade-create-options.component';
import { TradeCreateQuantityComponent } from '../trade-create-quantity/trade-create-quantity.component';
import { TradeCreateQuoteComponent } from '../trade-create-quote/trade-create-quote.component';
import { TradeCreateVolumeComponent } from '../trade-create-volume/trade-create-volume.component';

@Component({
  selector: 'trade-create',
  imports: [
    UserNotesComponent,
    TradingSessionComponent,
    FormsModule,
    ReactiveFormsModule,
    TradeCreateHeaderComponent,
    TradeCreateAssetComponent,
    TradeCreateHintComponent,
    TradeCreateQuoteComponent,
    TradeCreateQuantityComponent,
    TradeCreateOptionsComponent,
    TradeCreateVolumeComponent,
    UserMessagesComponent,
    AsyncPipe,
    TranslocoPipe,
    ButtonComponent,
    UpperCasePipe,
    HasFractionPipe,
    DonauCapitalHintComponent,
  ],
  templateUrl: './trade-create.component.html',
  styleUrl: './trade-create.component.scss',
})
export class TradeCreateComponent extends TradeCreatePageBase implements OnInit, OnChanges, OnDestroy {
  @Input() public isin?: string;
  @Input() public useCache = false;

  @Output() public goNext = new EventEmitter<void>();

  public readonly contractConfirmationFacade = inject(ContractConfirmationFacade);

  readonly #userMessagesService = inject(UserMessagesHolderService);

  public tooltip = {
    left: '0px',
    top: '0px',
  };

  public shouldConfirmMiCARContracts$ = combineLatest([
    this.tradeService.asset$,
    this.contractConfirmationFacade.shouldDisplayNudgingNotification$,
  ]).pipe(
    map(([asset, shouldDisplayNudgingNotification]) => asset?.instrumentType === InstrumentType.Crypto && shouldDisplayNudgingNotification),
  );

  public ngOnInit(): void {
    const { queryParams } = this.activatedRoute;
    queryParams.subscribe(() => {
      this.tradeService.reset();
      this.init();
    });

    this.preset(this.useCache);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    changes['isin']?.currentValue && this.presetByInput(changes['isin'].currentValue);
  }

  @HostListener('document:mousemove', ['$event'])
  public onMouseMove(e: { pageX: number; pageY: number }) {
    if (this.hasTradingSession) {
      return;
    }
    this.tooltip.left = `${e.pageX - 100}px`;
    this.tooltip.top = `${e.pageY - 65}px`;
  }

  public ngOnDestroy(): void {
    this.useCache = false;
    this.destroy();
  }

  public override submit(): void {
    super.submit();
    merge(this.tradeFacade.isOrderVerified$, this.tradeFacade.isCombinedOrderVerified$)
      .pipe(
        filter((isOrderVerified) => !!isOrderVerified),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.#userMessagesService.clearAllMessages();
        this.goNext.emit();
      });
  }
}
